/**
 * Feedback Detail Component
 * Modal showing detailed view of a feedback item
 */

import React from 'react';
import { cn } from '../../../lib/utils';
import { FeedbackWithStatus, FeedbackType } from '../../../types/feedback';

interface FeedbackDetailProps {
  feedback: FeedbackWithStatus;
  onClose: () => void;
  onDelete?: (feedbackId: string) => Promise<void>;
  onArchive?: (feedbackId: string) => Promise<void>;
  onStatusChange?: (feedbackId: string, status: string) => Promise<void>;
}

const FeedbackDetail: React.FC<FeedbackDetailProps> = ({
  feedback,
  onClose,
  onDelete,
  onArchive,
  onStatusChange
}) => {
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isArchiving, setIsArchiving] = React.useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = React.useState(false);

  const handleDelete = async () => {
    if (!onDelete || !window.confirm('Are you sure you want to delete this feedback? This action cannot be undone.')) return;
    
    setIsDeleting(true);
    try {
      await onDelete(feedback.id);
      onClose();
    } catch (error) {
      console.error('Failed to delete feedback:', error);
      alert('Failed to delete feedback. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleArchive = async () => {
    if (!onArchive || !window.confirm('Are you sure you want to archive this feedback?')) return;
    
    setIsArchiving(true);
    try {
      await onArchive(feedback.id);
      onClose();
    } catch (error) {
      console.error('Failed to archive feedback:', error);
      alert('Failed to archive feedback. Please try again.');
    } finally {
      setIsArchiving(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!onStatusChange) return;
    
    setIsUpdatingStatus(true);
    try {
      await onStatusChange(feedback.id, newStatus);
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };
  const getTypeLabel = (type: FeedbackType): string => {
    const labels: Record<FeedbackType, string> = {
      'quick-reaction': 'Quick Reaction',
      'page-comment': 'Page Comment',
      'element-feedback': 'Element Feedback',
      'flow-rating': 'Flow Rating',
      'bug-report': 'Bug Report',
      'feature-request': 'Feature Request',
      'general': 'General Feedback'
    };
    return labels[type];
  };

  const renderFeedbackContent = () => {
    switch (feedback.type) {
      case 'quick-reaction':
        const reactionData = feedback as any;
        return (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-1">Reaction</h4>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-terracotta-50 text-terracotta-700">
                {reactionData.reaction}
              </span>
            </div>
            {reactionData.comment && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Comment</h4>
                <p className="text-sm text-slate-600 bg-stone-50 p-3 rounded-lg">
                  {reactionData.comment}
                </p>
              </div>
            )}
          </div>
        );

      case 'page-comment':
        const commentData = feedback as any;
        return (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Category</h4>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-steel-50 text-steel-700">
                {commentData.category}
              </span>
            </div>
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Comment</h4>
              <p className="text-sm text-slate-600 bg-stone-50 p-3 rounded-lg leading-relaxed">
                {commentData.comment}
              </p>
            </div>
          </div>
        );

      case 'bug-report':
        const bugData = feedback as any;
        return (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Title</h4>
              <p className="text-sm font-medium text-slate-800">{bugData.title}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Description</h4>
              <p className="text-sm text-slate-600 bg-stone-50 p-3 rounded-lg leading-relaxed">
                {bugData.description}
              </p>
            </div>
            {bugData.stepsToReproduce && bugData.stepsToReproduce.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Steps to Reproduce</h4>
                <ol className="text-sm text-slate-600 space-y-1">
                  {bugData.stepsToReproduce.map((step: string, index: number) => (
                    <li key={index} className="flex gap-2">
                      <span className="font-medium text-terracotta-600">{index + 1}.</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-1">Severity</h4>
              <span className={cn(
                'inline-flex items-center px-3 py-1 rounded-full text-sm',
                bugData.severity === 'low' && 'bg-sage-50 text-sage-700',
                bugData.severity === 'medium' && 'bg-amber-50 text-amber-700',
                bugData.severity === 'high' && 'bg-coral-50 text-coral-700',
                bugData.severity === 'critical' && 'bg-red-50 text-red-700'
              )}>
                {bugData.severity}
              </span>
            </div>
          </div>
        );

      case 'feature-request':
        const featureData = feedback as any;
        return (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Title</h4>
              <p className="text-sm font-medium text-slate-800">{featureData.title}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Description</h4>
              <p className="text-sm text-slate-600 bg-stone-50 p-3 rounded-lg leading-relaxed">
                {featureData.description}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Use Case</h4>
              <p className="text-sm text-slate-600 bg-stone-50 p-3 rounded-lg leading-relaxed">
                {featureData.useCase}
              </p>
            </div>
          </div>
        );

      case 'general':
        const generalData = feedback as any;
        return (
          <div className="space-y-4">
            {generalData.subject && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Subject</h4>
                <p className="text-sm font-medium text-slate-800">{generalData.subject}</p>
              </div>
            )}
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Category</h4>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-stone-50 text-stone-700">
                {generalData.category}
              </span>
            </div>
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Message</h4>
              <p className="text-sm text-slate-600 bg-stone-50 p-3 rounded-lg leading-relaxed">
                {generalData.message}
              </p>
            </div>
          </div>
        );

      case 'element-feedback':
        const elementData = feedback as any;
        return (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Feedback Text</h4>
              <p className="text-sm text-slate-600 bg-stone-50 p-3 rounded-lg leading-relaxed">
                {elementData.feedbackText}
              </p>
            </div>
            {elementData.elementBounds && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Selected Area</h4>
                <div className="text-sm text-slate-600 bg-stone-50 p-3 rounded-lg">
                  <p>Position: ({Math.round(elementData.elementBounds.x)}, {Math.round(elementData.elementBounds.y)})</p>
                  <p>Size: {Math.round(elementData.elementBounds.width)}×{Math.round(elementData.elementBounds.height)}px</p>
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-3">
              {elementData.isVisualBug && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-coral-50 text-coral-700">
                  Visual Issue
                </span>
              )}
              {elementData.isFunctionalBug && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-50 text-red-700">
                  Functional Issue
                </span>
              )}
              {elementData.needsImprovement && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-amber-50 text-amber-700">
                  Needs Improvement
                </span>
              )}
            </div>
          </div>
        );

      case 'flow-rating':
        const flowData = feedback as any;
        return (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Flow</h4>
              <p className="text-sm text-slate-800">{flowData.flowName}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Rating</h4>
              <div className="flex items-center gap-2">
                <div className="flex text-amber-400">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      className={i < flowData.rating ? 'fill-current' : 'text-stone-300'}
                    >
                      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" fill="currentColor" />
                    </svg>
                  ))}
                </div>
                <span className="text-sm text-slate-600">({flowData.rating}/5)</span>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Completion Status</h4>
              <span className={cn(
                'inline-flex items-center px-3 py-1 rounded-full text-sm',
                flowData.completionStatus === 'completed' && 'bg-sage-50 text-sage-700',
                flowData.completionStatus === 'abandoned' && 'bg-coral-50 text-coral-700',
                flowData.completionStatus === 'helped-needed' && 'bg-amber-50 text-amber-700'
              )}>
                {flowData.completionStatus}
              </span>
            </div>
            {flowData.suggestions && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Suggestions</h4>
                <p className="text-sm text-slate-600 bg-stone-50 p-3 rounded-lg leading-relaxed">
                  {flowData.suggestions}
                </p>
              </div>
            )}
          </div>
        );

      default:
        return (
          <p className="text-sm text-slate-600">
            No detailed view available for this feedback type.
          </p>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900 bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <div>
            <h2 className="text-xl font-serif font-medium text-slate-800">
              {getTypeLabel(feedback.type)}
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              {new Date(feedback.timestamp).toLocaleString()}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 transition-colors rounded-lg hover:bg-stone-50"
            aria-label="Close modal"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18"/>
              <path d="M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          {renderFeedbackContent()}

          {/* Screenshot Display */}
          {((feedback as any).thumbnailBase64 || (feedback as any).screenshotUrl) && (
            <div className="mt-6 pt-6 border-t border-stone-200">
              <h4 className="text-sm font-medium text-slate-700 mb-3">Screenshot</h4>
              <div className="bg-stone-50 p-4 rounded-lg">
                <img
                  src={(feedback as any).thumbnailBase64 ? 
                    `data:image/png;base64,${(feedback as any).thumbnailBase64}` : 
                    (feedback as any).screenshotUrl
                  }
                  alt="Feedback screenshot"
                  className="max-w-full h-auto rounded-lg border border-stone-200 shadow-sm"
                />
                {(feedback as any).elementBounds && feedback.type === 'element-feedback' && (
                  <div className="mt-3 text-xs text-slate-600">
                    <p>Selected area: ({Math.round((feedback as any).elementBounds.x)}, {Math.round((feedback as any).elementBounds.y)}) - 
                    {Math.round((feedback as any).elementBounds.width)}×{Math.round((feedback as any).elementBounds.height)}px</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="mt-6 pt-6 border-t border-stone-200">
            <h4 className="text-sm font-medium text-slate-700 mb-3">Metadata</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-600">Page:</span>
                <p className="text-slate-800 break-all">{new URL(feedback.pageUrl).pathname}</p>
              </div>
              <div>
                <span className="text-slate-600">Priority:</span>
                <p className="text-slate-800 capitalize">{feedback.priority}</p>
              </div>
              {feedback.viewport && (
                <div>
                  <span className="text-slate-600">Viewport:</span>
                  <p className="text-slate-800">{feedback.viewport.width}×{feedback.viewport.height}</p>
                </div>
              )}
              <div>
                <span className="text-slate-600">Status:</span>
                <p className="text-slate-800 capitalize">{feedback.status}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-stone-200 bg-stone-50">
          <div className="flex items-center gap-3">
            {/* Status Change Dropdown */}
            {onStatusChange && (
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-700">Status:</label>
                <select
                  value={feedback.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={isUpdatingStatus}
                  className="text-sm border border-stone-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta disabled:bg-gray-100"
                >
                  <option value="new">New</option>
                  <option value="reviewing">Reviewing</option>
                  <option value="in-progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Archive Button */}
            {onArchive && feedback.status !== 'archived' && (
              <button
                onClick={handleArchive}
                disabled={isArchiving}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {isArchiving ? (
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r={10} stroke="currentColor" strokeWidth="4" fill="none" className="opacity-25"/>
                    <path fill="currentColor" className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="21,8 21,21 3,21 3,8"/>
                    <rect x="1" y="3" width="22" height="5"/>
                    <line x1="10" y1="12" x2="14" y2="12"/>
                  </svg>
                )}
                Archive
              </button>
            )}

            {/* Delete Button */}
            {onDelete && (
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {isDeleting ? (
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r={10} stroke="currentColor" strokeWidth="4" fill="none" className="opacity-25"/>
                    <path fill="currentColor" className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3,6 5,6 21,6"/>
                    <path d="m19,6v14a2,2 0 01-2,2H7a2,2 0 01-2-2V6m3,0V4a2,2 0 012-2h4a2,2 0 012,2v2"/>
                    <line x1="10" y1="11" x2="10" y2="17"/>
                    <line x1="14" y1="11" x2="14" y2="17"/>
                  </svg>
                )}
                Delete
              </button>
            )}

            <button
              onClick={onClose}
              className="btn bg-stone-500 text-white hover:bg-stone-600"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackDetail;